// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/powersync/error-handler.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/powersync/src/connector/error-handler.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo gym-log-app
//     node fleet/fleet.ts check --repo gym-log-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Sync error types.
 *
 * Used to classify errors and decide how to handle them:
 * - RETRYABLE: Retry the operation (network, timeout)
 * - CONFLICT: Discard local changes (already exists, not found)
 * - VALIDATION: Discard local changes (invalid data)
 * - PERMISSION: Propagate error (unauthorized, forbidden)
 * - UNKNOWN: Propagate error (could not be classified)
 *
 * ⚠️ CONFLICT and VALIDATION **discard the user's write**
 * (`transaction.complete()` in `BasePowerSyncConnector.handleError`). It's an
 * IRREVERSIBLE decision: the data doesn't come back. RETRYABLE / PERMISSION / UNKNOWN
 * `throw`, which keeps the transaction in the local queue.
 *
 * **Guiding criterion: when in doubt, retry; never discard.** One retry too
 * many costs bandwidth; one discard too few costs the user's data.
 */
export enum SyncErrorType {
  RETRYABLE = "retryable",
  CONFLICT = "conflict",
  VALIDATION = "validation",
  PERMISSION = "permission",
  UNKNOWN = "unknown",
}

const HTTP_SERVER_ERROR_MIN = 500;
const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_CONFLICT = 409;
const HTTP_GONE = 410;
const HTTP_UNPROCESSABLE_ENTITY = 422;
const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Write-path error that **preserves the HTTP status code**.
 *
 * It exists because serializing the response to text (`JSON.stringify(error)`) drops
 * the status, and without status the classification degrades to guessing by substrings.
 */
export class SyncHttpError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number, options?: ErrorOptions) {
    super(message, options);
    this.name = "SyncHttpError";
    this.status = status;
  }
}

/**
 * Extracts the HTTP status code from an error, if it carries one.
 *
 * Accepts both `error.status` (what `toSyncError` attaches) and
 * `error.response.status` (the shape used by openapi-fetch and several clients).
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const direct = (error as { status?: unknown }).status;
  if (typeof direct === "number" && Number.isFinite(direct)) {
    return direct;
  }

  const { response } = error as { response?: unknown };
  if (typeof response === "object" && response !== null) {
    const nested = (response as { status?: unknown }).status;
    if (typeof nested === "number" && Number.isFinite(nested)) {
      return nested;
    }
  }

  return undefined;
}

/**
 * Normalizes a request failure into an `Error` that **carries the HTTP status**.
 *
 * The status is attached as a PROPERTY, never concatenated into the message: putting
 * the number back into the prose would reproduce exactly the class of bug this
 * eliminates (an accidental `404` inside the text discarding writes).
 */
export function toSyncError(cause: unknown, status?: number): Error {
  const resolvedStatus = status ?? getErrorStatus(cause);

  if (cause instanceof Error) {
    if (resolvedStatus !== undefined && getErrorStatus(cause) === undefined) {
      Object.defineProperty(cause, "status", {
        value: resolvedStatus,
        writable: true,
        enumerable: true,
        configurable: true,
      });
    }
    return cause;
  }

  return new SyncHttpError(JSON.stringify(cause), resolvedStatus, { cause });
}

/**
 * Classifies by HTTP status code. This is the AUTHORITATIVE path: the status is
 * set by the protocol, not by the prose an operator writes on a dashboard.
 *
 * - **5xx ⇒ ALWAYS `RETRYABLE`**, whatever the text says: the server is saying
 *   "not now", not "your data is wrong".
 * - `408` / `429` are transient by definition.
 * - `401` / `403` ⇒ `PERMISSION` (propagates, doesn't discard).
 * - `404` / `409` / `410` ⇒ `CONFLICT`: the row no longer exists or collided. Discarding
 *   here is INTENTIONAL (something deleted on another device cannot be retried
 *   forever).
 * - `400` / `422` ⇒ `VALIDATION`: the payload is not acceptable and never will be;
 *   retrying it clogs the head of the queue and blocks EVERYTHING behind it.
 * - Anything else ⇒ `UNKNOWN`, which propagates (= retries). When in doubt, don't
 *   discard.
 */
export function classifyStatus(status: number): SyncErrorType {
  if (
    status >= HTTP_SERVER_ERROR_MIN ||
    status === HTTP_REQUEST_TIMEOUT ||
    status === HTTP_TOO_MANY_REQUESTS
  ) {
    return SyncErrorType.RETRYABLE;
  }

  if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
    return SyncErrorType.PERMISSION;
  }

  if (status === HTTP_NOT_FOUND || status === HTTP_CONFLICT || status === HTTP_GONE) {
    return SyncErrorType.CONFLICT;
  }

  if (status === HTTP_BAD_REQUEST || status === HTTP_UNPROCESSABLE_ENTITY) {
    return SyncErrorType.VALIDATION;
  }

  return SyncErrorType.UNKNOWN;
}

/**
 * Checks whether the error is due to network problems (retryable).
 */
export function isRetryable(message: string): boolean {
  return (
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("econnrefused") ||
    message.includes("fetch failed")
  );
}

/**
 * `404` written as a status code, NOT as a bare number.
 *
 * The previous version was `message.includes("404")`, which matched inside
 * `Retry-After: 3404`, an incident id, or a date — and every
 * match discarded the user's queued writes.
 */
const HTTP_404_IN_TEXT = /\b(?:http|https|status(?:\s*code)?|code|error)\s*:?\s*404\b/;

/**
 * Checks whether the error is a conflict (discard local changes).
 *
 * ⚠️ DISCARD path: only unambiguous markers. Used as a fallback when
 * the error doesn't carry an HTTP status.
 */
export function isConflict(message: string): boolean {
  return (
    message.includes("conflict") ||
    message.includes("already exists") ||
    message.includes("duplicate") ||
    message.includes("not found") ||
    HTTP_404_IN_TEXT.test(message)
  );
}

/**
 * Validation verdicts about a specific field.
 *
 * The previous version was plain `message.includes("invalid")`, which matched
 * any prose ("window invalid", "state invalid") and discarded the
 * write. Now the word has to be qualified by what is invalid.
 */
const VALIDATION_PATTERNS = [
  /\bvalidation\b/,
  /\bis required\b/,
  /\bmissing required\b/,
  /\brequired (?:field|fields|parameter|parameters|property|value)\b/,
  /\binvalid (?:input|value|values|format|field|fields|data|request|payload|parameter|parameters|argument|arguments|body|json|email|password|id|uuid|token|type|schema|syntax|column|row|record)\b/,
];

/**
 * Checks whether the error is a validation error (discard local changes).
 *
 * ⚠️ DISCARD path: only unambiguous markers. Used as a fallback when
 * the error doesn't carry an HTTP status.
 */
export function isValidation(message: string): boolean {
  return VALIDATION_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Checks whether the error is a permission error (propagate error).
 */
export function isPermission(message: string): boolean {
  return (
    message.includes("unauthorized") ||
    message.includes("forbidden") ||
    message.includes("401") ||
    message.includes("403")
  );
}

/**
 * Classifies an error by its type to determine how to handle it.
 *
 * Decision order:
 * 1. **If the error carries an HTTP status, the status rules and NOTHING else.** The text
 *    is never looked at again: a message worded by an operator cannot trigger
 *    a discard.
 * 2. Without a status (network error, non-HTTP failure), the historical text
 *    cascade is kept, with the two accidental substrings already hardened.
 *
 * @param error - The error to classify
 * @returns The error type
 */
export function classifyError(error: unknown): SyncErrorType {
  if (!(error instanceof Error)) {
    return SyncErrorType.UNKNOWN;
  }

  const status = getErrorStatus(error);
  if (status !== undefined) {
    return classifyStatus(status);
  }

  const message = error.message.toLowerCase();

  if (isRetryable(message)) {
    return SyncErrorType.RETRYABLE;
  }

  if (isConflict(message)) {
    return SyncErrorType.CONFLICT;
  }

  if (isValidation(message)) {
    return SyncErrorType.VALIDATION;
  }

  if (isPermission(message)) {
    return SyncErrorType.PERMISSION;
  }

  return SyncErrorType.UNKNOWN;
}
